import { DialogService } from '@sdbn/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, ContextChange, DictionaryEntry, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, UserProfileDialogService, WidgetStyle } from '@sdbn/common';
import { Subscription } from 'rxjs';

import { IssueType } from '../model/issue-type';
import { IssueTypeDetails } from '../model/issue-type-details';
import { IssueTypeAdministrationService } from '../service/issue-type-administration.service';

@Component({
    selector: 'app-issue-type',
    templateUrl: './issue-type.component.html',
    styleUrls: ['./issue-type.component.scss'],
    standalone: false
})
export class IssueTypeComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  issueTypes: IssueType[] = [];

  selected: IssueType;
  selectedDetails: IssueTypeDetails = new IssueTypeDetails();
  workingCopyDetails: IssueTypeDetails = new IssueTypeDetails();

  responseMessages: ResponseMessage[];
  isLoading = false;

  get cancelEnabled() {
    return !this.isLoading && this.form != null && this.form.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.form != null && this.form.dirty && this.form.valid;
  }

  @ViewChild('displayForm') form: NgForm;

  languageSubscription: Subscription;

  constructor(
    private issueTypeService: IssueTypeAdministrationService,
    private dialog: MatDialog,
    public translationService: TranslationService,
    public userProfileDialogService: UserProfileDialogService,
    private dialogService: DialogService) {

    super();

    this.selectedDetails = this.createEmptyDetails();
    this.workingCopyDetails = this.createEmptyDetails();

    this.languageSubscription = this.translationService.changed.subscribe(language => {
      this.createTranslatedValuesOnList();
    });

  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.languageSubscription != null) {
      this.languageSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.load();
  }

  createTranslatedValuesOnList() {
    for (const supplementaryEquipment of this.issueTypes) {
      supplementaryEquipment.translatedLabel = this.translationService.translate(supplementaryEquipment.nameDictionaryKey);
    }
  }

  setSelected(issueType: IssueType) {

    if (this.selected != null && this.selected.id === issueType.id) {
      return;
    }

    if (this.form?.form?.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.setSelectedWithoutCheck(issueType);
        }
      });
    } else {
      this.setSelectedWithoutCheck(issueType);
    }

  }

  setSelectedWithoutCheck(issueType: IssueType) {
    this.selected = issueType;
    this.setLoading(true);
    this.responseMessages = [];
    if (this.form != null) {
      this.form.resetForm();
    }
    if (issueType != null) {
      this.issueTypeService.getIssueTypeDetails({ id: issueType.id }).subscribe(details => {
        if (details != null) {
          this.selectedDetails = details;
          this.workingCopyDetails = JSON.parse(JSON.stringify(details));
        } else {
          this.clear();
        }
        this.setLoading(false);
      }, error => {
        this.setLoading(false);
        console.log(error);
      });
    } else {
      this.setLoading(false);
    }
  }

  isSelected(details: IssueType) {
    return this.selectedDetails != null && this.selectedDetails.id === details.id;
  }

  delete(issueType: IssueType) {

    this.responseMessages = [];


    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.issue-type.delete.confirm-dialog.title',
      'tmc-yard.ui.admin.issue-type.delete.confirm-dialog.message'
    ).afterClosed().subscribe(confirm => {

      if (confirm) {
        this.setLoading(true);

        this.issueTypeService.deleteIssueType({ id: issueType.id }).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.setLoading(false);
        }, error => {
          console.log(error);
          this.setLoading(false);
        });
      }
    });
  }

  load() {
    this.setLoading(true);
    this.responseMessages = [];
    this.issueTypeService.getIssueTypes().subscribe(issueTypes => {

      const newIssueTypes = new Array<IssueType>();
      for (const issueType of issueTypes) {
        newIssueTypes.push(new IssueType(issueType));
      }
      this.issueTypes = newIssueTypes;

      if (issueTypes.length > 0) {
        if (this.selected == null) {
          this.setSelectedWithoutCheck(this.issueTypes[0]);
        } else {
          const oldSelected = this.issueTypes.find(s => s.id === this.selected.id);
          if (oldSelected != null) {
            this.setSelectedWithoutCheck(oldSelected);
          } else {
            this.setSelectedWithoutCheck(this.issueTypes[0]);
          }
        }
      }

      this.createTranslatedValuesOnList();

      this.setLoading(false);
    }, error => {
      console.error(error);
      this.setLoading(false);
    });
  }

  addNew() {
    if (this.form.form.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.clear();
        }
      });

    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.form.form.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.issue-type.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.discardChanges();
        }
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    if (this.workingCopyDetails.id === -1) {
      this.clear();
    } else {
      this.setSelectedWithoutCheck(this.selected);
    }
  }

  private clear() {
    this.responseMessages = [];
    this.selected = null;

    this.selectedDetails = this.createEmptyDetails();

    this.workingCopyDetails = JSON.parse(JSON.stringify(this.selectedDetails));
    this.form.resetForm({});
  }

  private createEmptyDetails(): IssueTypeDetails {
    const details = new IssueTypeDetails();
    details.name = new DictionaryEntry();
    details.id = -1;

    return details;
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  save() {
    this.setLoading(true);
    this.responseMessages = [];
    if (this.workingCopyDetails.id === -1) {
      this.createDetails(this.workingCopyDetails);
    } else {
      this.updateDetails(this.workingCopyDetails);
    }
  }

  private updateDetails(detailsToUpdate: IssueTypeDetails) {
    this.issueTypeService.saveIssueType(detailsToUpdate).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.selectedDetails = JSON.parse(JSON.stringify(this.workingCopyDetails));
        this.form.resetForm();

        this.load();

      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  private createDetails(detailsToAdd: IssueTypeDetails) {
    this.issueTypeService.createIssueType(detailsToAdd).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.clearAndLoad();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }


  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as IssueType);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as IssueType);
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  public onDictionaryChanged() {
    this.form.form.markAsDirty();
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
